/**
 * QueryBuilder Store - persist migrations.
 *
 * Owns the localStorage persist envelope: the storage key, the current
 * schema version, the `migrate` ladder that upgrades pre-v10 states, and
 * the `partialize` projection of what gets persisted. `migrate` is a pure
 * function so it can be unit-tested without touching the store.
 */
import {
  BuilderTab,
  TransformPipeline,
  TransformStep,
} from "types/queryBuilder.types"
import { QueryBuilderStore } from "./types"
import { defaultBuilderOutputName, newId } from "./helpers"

export const QUERY_BUILDER_PERSIST_NAME = "dataq-query-builder"
export const QUERY_BUILDER_PERSIST_VERSION = 10

// Pre-v5 had `recodes: Recode[]` either nested under each builder
// (very old) or at top level (v4). Hoist + convert each into a
// CombineOrStep, dedup by name (first wins).
export const migrateQueryBuilderState = (
  persistedState: unknown
): QueryBuilderStore => {
  type LegacyRecode = {
    id?: string
    name?: string
    sourceVariables?: string[]
  }
  type LegacyBuilder = BuilderTab & {
    recodes?: LegacyRecode[]
    // Pre-v8 boolean toggle replaced by transformPipelineId.
    applyTransforms?: boolean
  }
  type LegacyState = {
    projectId?: string | null
    builders?: LegacyBuilder[]
    activeBuilderId?: string | null
    recodes?: LegacyRecode[]
    transformSteps?: TransformStep[]
    // v8+:
    transformPipelines?: TransformPipeline[]
    activeTransformPipelineId?: string | null
  }
  const state = (persistedState ?? {}) as LegacyState
  const seenNames = new Set<string>()
  const collected: TransformStep[] = []

  const ingest = (recodes: LegacyRecode[] | undefined) => {
    if (!recodes) return
    for (const r of recodes) {
      const name = (r.name ?? "").trim()
      if (!name || seenNames.has(name)) continue
      seenNames.add(name)
      collected.push({
        id: r.id ?? newId(),
        type: "combine_or",
        mode: "create",
        name,
        sources: r.sourceVariables ?? [],
      })
    }
  }
  ingest(state.recodes)

  // v6→v7 step mode backfill (used both for pre-v8 transformSteps
  // and steps already inside a v8 pipeline). Also handles v8→v9:
  // LoopFieldIteration.excludeNone → excludedValues (a list, so users
  // can drop "None", "Other", "Refused", etc.). Recurses into loop
  // bodies because nested loops carry the same shape.
  const backfillStepMode = (raw: TransformStep): TransformStep => {
    const s = raw as unknown as Record<string, unknown> & {
      type: string
    }
    if (
      (s.type === "combine_or" ||
        s.type === "and_combine" ||
        s.type === "negate") &&
      s.mode === undefined
    ) {
      return { ...s, mode: "create" } as unknown as TransformStep
    }
    if (s.type === "loop") {
      const loop = s as unknown as Record<string, unknown> & {
        iteration?: Record<string, unknown>
        body?: TransformStep[]
      }
      const it = loop.iteration
      if (
        it &&
        it.kind === "field" &&
        "excludeNone" in it &&
        !("excludedValues" in it)
      ) {
        const next: Record<string, unknown> = { ...it }
        next.excludedValues = it.excludeNone ? ["None"] : []
        delete next.excludeNone
        loop.iteration = next
      }
      if (Array.isArray(loop.body)) {
        loop.body = loop.body.map(backfillStepMode)
      }
      return loop as unknown as TransformStep
    }
    return raw
  }

  // v7→v8: wrap the single `transformSteps` array in a "Default"
  // pipeline. Builders' `applyTransforms` boolean translates to
  // pointing at that pipeline (true) or null (false).
  let pipelines: TransformPipeline[]
  let activePipelineId: string | null
  if (state.transformPipelines && state.transformPipelines.length > 0) {
    pipelines = state.transformPipelines.map((p) => ({
      ...p,
      steps: p.steps.map(backfillStepMode),
    }))
    activePipelineId =
      state.activeTransformPipelineId ?? pipelines[0].id
  } else {
    const defaultPipeline: TransformPipeline = {
      id: newId(),
      name: "Default",
      steps: (state.transformSteps ?? collected).map(backfillStepMode),
    }
    pipelines = [defaultPipeline]
    activePipelineId = defaultPipeline.id
  }
  const defaultBindingId = pipelines[0]?.id ?? null

  // v9→v10: backfill `outputName` (engine QTable id) from the builder
  // name. Pre-v10 builders generated a fresh `tbl_<sanitised>_<ts>`
  // per run; from v10 the user picks (and renames) it, and it's
  // persisted. Collisions are resolved by appending `_<n>` in
  // declaration order during the migration walk.
  const acc: BuilderTab[] = []
  const builders: BuilderTab[] = (state.builders ?? []).map((b) => {
    const existing = b as BuilderTab & { outputName?: string }
    const outputName =
      existing.outputName && existing.outputName.length > 0
        ? existing.outputName
        : defaultBuilderOutputName(b.name, acc)
    const next: BuilderTab = {
      id: b.id,
      name: b.name,
      outputName,
      operation: b.operation,
      rowVariables: b.rowVariables,
      columnVariables: b.columnVariables,
      selectedOperators: b.selectedOperators,
      universeFilter: b.universeFilter,
      filterGroups: b.filterGroups ?? [],
      isTransposed: b.isTransposed,
      // v8 mapping: applyTransforms=true → bind to default pipeline;
      // false → null (no transforms). Existing v8 ids pass through.
      transformPipelineId:
        b.transformPipelineId !== undefined
          ? b.transformPipelineId
          : b.applyTransforms === false
          ? null
          : defaultBindingId,
    }
    acc.push(next)
    return next
  })
  if (builders.length === 0) {
    ingest(undefined) // no-op, but keeps `ingest` referenced
  }
  // Drain any nested legacy recodes from each builder.
  ;(state.builders ?? []).forEach((b) => ingest(b.recodes))

  return {
    projectId: state.projectId ?? null,
    builders,
    activeBuilderId: state.activeBuilderId ?? null,
    transformPipelines: pipelines,
    activeTransformPipelineId: activePipelineId,
    // v8 added: backend-saved tracking + migration flag. Pre-v8 state
    // is treated as "not yet migrated" so the next project visit will
    // sync local pipelines/builders to the backend if any exist.
    pipelineSavedNames: {},
    builderSavedNames: {},
    migratedV1: false,
  } as QueryBuilderStore
}

export const partializeQueryBuilderState = (state: QueryBuilderStore) => ({
  projectId: state.projectId,
  builders: state.builders,
  activeBuilderId: state.activeBuilderId,
  transformPipelines: state.transformPipelines,
  activeTransformPipelineId: state.activeTransformPipelineId,
  pipelineSavedNames: state.pipelineSavedNames,
  builderSavedNames: state.builderSavedNames,
  migratedV1: state.migratedV1,
})
